
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MessageCircle, Users, Clock, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface DocumentCollaborationProps {
  documentId: string;
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profile?: {
    full_name: string | null;
    email: string | null;
    avatar_url?: string | null;
  } | null;
}

interface ActiveUser {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url?: string | null;
  last_seen: string;
}

export const DocumentCollaboration = ({ documentId }: DocumentCollaborationProps) => {
  const { toast } = useToast();
  const [comments, setComments] = useState<Comment[]>([]);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadComments();
    loadActiveUsers();
    updateUserPresence();

    // Set up real-time subscriptions
    const commentsChannel = supabase
      .channel(`document_comments_${documentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_comments',
          filter: `document_id=eq.${documentId}`,
        },
        () => {
          loadComments();
        }
      )
      .subscribe();

    const presenceChannel = supabase
      .channel(`document_presence_${documentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_activity',
          filter: `document_id=eq.${documentId}`,
        },
        () => {
          loadActiveUsers();
        }
      )
      .subscribe();

    // Update presence every 30 seconds
    const presenceInterval = setInterval(updateUserPresence, 30000);

    return () => {
      commentsChannel.unsubscribe();
      presenceChannel.unsubscribe();
      clearInterval(presenceInterval);
    };
  }, [documentId]);

  const loadComments = async () => {
    try {
      // First get comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('document_comments')
        .select('*')
        .eq('document_id', documentId)
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;

      // Then get profile data for each comment
      const commentsWithProfiles = await Promise.all(
        (commentsData || []).map(async (comment) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email, avatar_url')
            .eq('id', comment.user_id)
            .single();

          return {
            ...comment,
            profile
          };
        })
      );

      setComments(commentsWithProfiles);
    } catch (error: any) {
      console.error('Error loading comments:', error);
    }
  };

  const loadActiveUsers = async () => {
    try {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      
      // Get activity data
      const { data: activityData, error: activityError } = await supabase
        .from('document_activity')
        .select('user_id, last_seen')
        .eq('document_id', documentId)
        .gte('last_seen', fiveMinutesAgo);

      if (activityError) throw activityError;

      // Get profile data for active users
      const usersWithProfiles = await Promise.all(
        (activityData || []).map(async (activity) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, email, avatar_url')
            .eq('id', activity.user_id)
            .single();

          return {
            id: activity.user_id,
            full_name: profile?.full_name || null,
            email: profile?.email || null,
            avatar_url: profile?.avatar_url || null,
            last_seen: activity.last_seen,
          };
        })
      );

      setActiveUsers(usersWithProfiles);
    } catch (error: any) {
      console.error('Error loading active users:', error);
    }
  };

  const updateUserPresence = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('document_activity')
        .upsert({
          document_id: documentId,
          user_id: user.id,
          last_seen: new Date().toISOString(),
        });
    } catch (error: any) {
      console.error('Error updating presence:', error);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('document_comments')
        .insert({
          document_id: documentId,
          user_id: user.id,
          content: newComment.trim(),
        });

      if (error) throw error;

      setNewComment("");
      toast({
        title: "Comment Added",
        description: "Your comment has been added successfully.",
      });
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Failed to add comment.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Active Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Active Users ({activeUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {activeUsers.map((user) => (
              <div key={user.id} className="flex items-center gap-2 bg-green-50 p-2 rounded-lg">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={user.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {user.full_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{user.full_name || user.email || 'Anonymous'}</span>
                <Badge variant="secondary" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  {formatDistanceToNow(new Date(user.last_seen), { addSuffix: true })}
                </Badge>
              </div>
            ))}
            {activeUsers.length === 0 && (
              <p className="text-sm text-gray-500">No other users currently viewing this document</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Comments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Comments ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Comment */}
          <div className="space-y-2">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
            />
            <Button 
              onClick={addComment} 
              disabled={isLoading || !newComment.trim()}
              size="sm"
            >
              <Send className="h-4 w-4 mr-2" />
              {isLoading ? "Adding..." : "Add Comment"}
            </Button>
          </div>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3 p-3 border rounded-lg">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={comment.profile?.avatar_url || undefined} />
                  <AvatarFallback>
                    {comment.profile?.full_name?.charAt(0).toUpperCase() || 
                     comment.profile?.email?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">
                      {comment.profile?.full_name || comment.profile?.email || 'Anonymous'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{comment.content}</p>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No comments yet. Be the first to add one!
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
